import { clsx } from 'clsx'
import { type FC, useMemo } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'

import { STORYBLOK_BMI_CALCULATOR_CATEGORY } from './STORYBLOK_BMI_CALCULATOR_CATEGORY'
import { STORYBLOK_BMI_CALCULATOR_LIMIT } from './STORYBLOK_BMI_CALCULATOR_LIMIT'

type StoryblokBmiCalculatorResultProps = {
  value?: number
}

export const StoryblokBmiCalculatorResult: FC<
  StoryblokBmiCalculatorResultProps
> = ({ value }) => {
  const categoryValue = useMemo(() => {
    if (!value) {
      return STORYBLOK_BMI_CALCULATOR_CATEGORY.NO_VALUE
    }

    if (value < STORYBLOK_BMI_CALCULATOR_LIMIT.UNDERWEIGHT) {
      return STORYBLOK_BMI_CALCULATOR_CATEGORY.UNDERWEIGHT
    }

    if (value < STORYBLOK_BMI_CALCULATOR_LIMIT.NORMAL_WEIGHT) {
      return STORYBLOK_BMI_CALCULATOR_CATEGORY.NORMAL_WEIGHT
    }

    if (value < STORYBLOK_BMI_CALCULATOR_LIMIT.OVERWEIGHT) {
      return STORYBLOK_BMI_CALCULATOR_CATEGORY.OVERWEIGHT
    }

    return STORYBLOK_BMI_CALCULATOR_CATEGORY.OBESITY
  }, [value])

  const category = (
    <FormattedMessage
      id="bmi.calculator.category"
      values={{ category: categoryValue }}
    />
  )

  if (!value) {
    return (
      <span className="text-3xl font-medium text-dark-primary-strong">
        {category}
      </span>
    )
  }

  return (
    <div
      className={clsx(
        'flex flex-col gap-text-xs',
        categoryValue === STORYBLOK_BMI_CALCULATOR_CATEGORY.NORMAL_WEIGHT &&
          'text-dark-success',
      )}
    >
      <span className="font-mono text-3xl">
        <FormattedNumber
          maximumFractionDigits={1}
          minimumFractionDigits={1}
          /*
           * Truncate the value for display when it is close to a range boundary, such as `24.96`.
           * Rounding it to `25` would incorrectly classify it as overweight instead of normal weight.
           */
          roundingMode="trunc"
          value={value}
        />
      </span>
      <span className="text-m">
        <FormattedMessage
          id="bmi.calculator.result"
          values={{
            category,
          }}
        />
      </span>
    </div>
  )
}
